<template>
  <div ref="chartRef" class="vod-category-progress-chart"></div>
</template>

<script setup>
/* global defineProps */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  height: { type: [Number, String], default: '100%' }
});

const chartRef = ref(null);
let chartInstance = null;

const chartRows = computed(() =>
  (props.rows || []).map((item) => ({
    label: item.label,
    total: item.total,
    closeRate: Number(item.closeRate || 0),
    pendingClarify: item.pendingClarify || 0,
    preparing: item.preparing || 0,
    inProgress: item.inProgress || 0,
    verifying: item.verifying || 0,
    closed: item.closed || 0,
    clarifyPctDisplay: Number(item.clarifyPct ?? 0),
    preparePctDisplay: Number(item.preparePct ?? 0),
    ongoingPctDisplay: Number(item.ongoingPct ?? 0),
    verifyPctDisplay: Number(item.verifyPct ?? 0),
    closePctDisplay: Number(item.closePct ?? 0),
    clarifyPctRaw: Number(item.clarifyPctRaw ?? item.clarifyPct ?? 0),
    preparePctRaw: Number(item.preparePctRaw ?? item.preparePct ?? 0),
    ongoingPctRaw: Number(item.ongoingPctRaw ?? item.ongoingPct ?? 0),
    verifyPctRaw: Number(item.verifyPctRaw ?? item.verifyPct ?? 0),
    closePctRaw: Number(item.closePctRaw ?? item.closePct ?? 0)
  }))
);

const normalizedHeight = computed(() =>
  typeof props.height === 'number' ? `${props.height}px` : props.height
);

const buildSeriesData = (fieldName) => chartRows.value.map((item) => item[fieldName]);

const tooltipFormatter = (params) => {
  const rowIndex = params?.[0]?.dataIndex ?? 0;
  const row = chartRows.value[rowIndex];
  if (!row) return '';
  return [
    `<strong>${row.label}</strong>`,
    `总计：${row.total.toLocaleString()}`,
    `${params[0].marker} 意向、待确认、已确认：${row.pendingClarify.toLocaleString()}（${row.clarifyPctRaw.toFixed(1)}%）`,
    `${params[1].marker} 准备中：${row.preparing.toLocaleString()}（${row.preparePctRaw.toFixed(1)}%）`,
    `${params[2].marker} 进行中：${row.inProgress.toLocaleString()}（${row.ongoingPctRaw.toFixed(1)}%）`,
    `${params[3].marker} 已修复、验收中、已验收：${row.verifying.toLocaleString()}（${row.verifyPctRaw.toFixed(1)}%）`,
    `${params[4].marker} 已闭环：${row.closed.toLocaleString()}（${row.closePctRaw.toFixed(1)}%）`,
    `闭环率：${row.closeRate.toFixed(1)}%`
  ].join('<br/>');
};

const buildOption = () => ({
  animation: true,
  animationDuration: 900,
  animationDurationUpdate: 500,
  animationEasing: 'cubicOut',
  animationDelay: (idx) => idx * 80,
  color: ['#f08a00', '#7b8ea8', '#4f8df6', '#5b6ce1', '#2fb98f'],
  legend: {
    top: 6,
    left: 10,
    icon: 'circle',
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 14,
    textStyle: { color: '#5f728d', fontSize: 12 },
    data: ['意向、待确认、已确认', '准备中', '进行中', '已修复、验收中、已验收', '已闭环']
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: tooltipFormatter,
    backgroundColor: 'rgba(17,24,39,0.92)',
    textStyle: { color: '#fff', fontSize: 12 },
    borderWidth: 0,
    extraCssText: 'text-align:left;'
  },
  grid: {
    left: 72,
    right: 92,
    top: 44,
    bottom: 8,
    containLabel: true
  },
  xAxis: {
    type: 'value',
    max: 100,
    axisLabel: { show: false },
    axisTick: { show: false },
    axisLine: { show: false },
    splitLine: { show: false }
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: chartRows.value.map((item) => item.label),
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: {
      color: '#1f3653',
      fontSize: 15,
      fontWeight: 700,
      margin: 10
    }
  },
  series: [
    {
      name: '意向、待确认、已确认',
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      data: buildSeriesData('clarifyPctDisplay')
    },
    {
      name: '准备中',
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      data: buildSeriesData('preparePctDisplay')
    },
    {
      name: '进行中',
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      data: buildSeriesData('ongoingPctDisplay')
    },
    {
      name: '已修复、验收中、已验收',
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      data: buildSeriesData('verifyPctDisplay')
    },
    {
      name: '已闭环',
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      data: buildSeriesData('closePctDisplay'),
      label: {
        show: true,
        position: 'right',
        distance: 6,
        color: '#5b7292',
        fontSize: 12,
        formatter: (params) => {
          const row = chartRows.value[params.dataIndex];
          if (!row) return '';
          return `${row.total.toLocaleString()}  ${row.closeRate.toFixed(1)}%`;
        }
      }
    }
  ]
});

const renderChart = () => {
  if (!chartRef.value) return;
  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value);
  }
  chartRef.value.style.height = normalizedHeight.value;
  chartInstance.setOption(buildOption(), true);
};

const resizeChart = () => {
  if (chartInstance) chartInstance.resize();
};

onMounted(() => {
  renderChart();
  window.addEventListener('resize', resizeChart);
});

watch(() => props.rows, renderChart, { deep: true });
watch(() => props.height, renderChart);

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeChart);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style scoped>
.vod-category-progress-chart {
  width: 100%;
  height: 100%;
}
</style>
